import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, getCurrentUser } from "@/lib/apiClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowUpCircle, ArrowDownCircle, RotateCcw, Loader2, Plus, Package } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLotsForMaterial, useRegisterLotMovement } from "@/hooks/useMaterialLots";

interface StockMovementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  materialId: string;
  materialName: string;
  currentStock: number;
  unit: string;
  movementType: "in" | "out" | "adjustment";
}

export function StockMovementDialog({
  open,
  onOpenChange,
  materialId,
  materialName,
  currentStock,
  unit,
  movementType,
}: StockMovementDialogProps) {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [lotMode, setLotMode] = useState<"existing" | "new">("existing");
  const [formData, setFormData] = useState({
    quantity: "",
    squareMeters: "",
    reason: "",
    notes: "",
    lotId: "",
    // For new lot
    batchNumber: "",
    rollNumber: "",
  });

  const { data: lots, isLoading: lotsLoading } = useLotsForMaterial(materialId);
  const registerLotMovement = useRegisterLotMovement();

  const selectedLot = lots?.find((lot) => lot.id === formData.lotId);
  const hasLots = lots && lots.length > 0;

  const inReasons = [
    { value: "purchase", label: "Compra" },
    { value: "return", label: "Devolución" },
    { value: "transfer_in", label: "Transferencia entrada" },
    { value: "production_return", label: "Retorno de producción" },
    { value: "initial", label: "Stock inicial" },
    { value: "other", label: "Otro" },
  ];

  const outReasons = [
    { value: "production", label: "Producción" },
    { value: "consumption", label: "Consumo" },
    { value: "damage", label: "Material dañado" },
    { value: "expired", label: "Vencido" },
    { value: "transfer_out", label: "Transferencia salida" },
    { value: "sample", label: "Muestra" },
    { value: "other", label: "Otro" },
  ];

  const adjustmentReasons = [
    { value: "correction", label: "Corrección de inventario" },
    { value: "recount", label: "Reconteo físico" },
    { value: "system_error", label: "Error de sistema" },
    { value: "other", label: "Otro" },
  ];

  const reasons =
    movementType === "in"
      ? inReasons
      : movementType === "out"
      ? outReasons
      : adjustmentReasons;

  const getTitle = () => {
    switch (movementType) {
      case "in":
        return "Registrar Entrada";
      case "out":
        return "Registrar Salida";
      case "adjustment":
        return "Ajuste de Inventario";
    }
  };

  const getIcon = () => {
    switch (movementType) {
      case "in":
        return <ArrowUpCircle className="w-5 h-5 text-green-500" />;
      case "out":
        return <ArrowDownCircle className="w-5 h-5 text-red-500" />;
      case "adjustment":
        return <RotateCcw className="w-5 h-5 text-blue-500" />;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const quantity = parseFloat(formData.quantity);
    if (isNaN(quantity) || quantity <= 0) {
      toast.error("Ingresa una cantidad válida");
      return;
    }

    if (!formData.reason) {
      toast.error("Selecciona un motivo");
      return;
    }

    // Validate based on lot mode
    if (lotMode === "existing" && hasLots && !formData.lotId) {
      toast.error("Selecciona un lote");
      return;
    }

    if (lotMode === "new" && !formData.batchNumber.trim()) {
      toast.error("Ingresa el número de lote");
      return;
    }

    // Validate stock for exit
    if (movementType === "out") {
      if (lotMode === "existing" && selectedLot && quantity > selectedLot.quantity) {
        toast.error(`La cantidad excede el stock del lote (${selectedLot.quantity} ${unit})`);
        return;
      }
      if (lotMode === "new") {
        toast.error("No puedes registrar salida de un lote nuevo. Selecciona un lote existente.");
        return;
      }
      if (!hasLots && quantity > currentStock) {
        toast.error("La cantidad excede el stock disponible");
        return;
      }
    }

    setLoading(true);
    try {
      const user = getCurrentUser();
      const sqmChange = formData.squareMeters ? parseFloat(formData.squareMeters) : undefined;

      // CASE 1: Using existing lot
      if (lotMode === "existing" && formData.lotId && selectedLot) {
        await registerLotMovement.mutateAsync({
          materialId,
          lotId: formData.lotId,
          movementType,
          quantity,
          squareMetersChange: sqmChange,
          reason: formData.reason,
          notes: formData.notes,
          currentLotStock: selectedLot.quantity,
          currentLotSquareMeters: selectedLot.square_meters,
        });
        toast.success(movementType === "in" ? "Entrada registrada" : "Salida registrada");
        onOpenChange(false);
        resetForm();
        return;
      }

      // CASE 2: Creating new lot (only for entries)
      if (lotMode === "new" && movementType === "in") {
        // Create the new lot
        const { data: newLot, error: lotError } = await api
          .from("material_lots")
          .insert({
            material_id: materialId,
            batch_number: formData.batchNumber.trim(),
            roll_number: formData.rollNumber.trim() || null,
            quantity: quantity,
            square_meters: sqmChange || 0,
            is_active: true,
            is_in_use: false,
          })
          .select()
          .single();

        if (lotError) throw lotError;

        // Calculate new total stock
        const newStock = currentStock + quantity;

        // Insert movement record
        const { error: movementError } = await api
          .from("inventory_movements")
          .insert({
            material_inventory_id: materialId,
            lot_id: newLot.id,
            movement_type: "in",
            quantity: quantity,
            reason: formData.reason,
            notes: formData.notes || null,
            performed_by: user?.id,
            previous_stock: currentStock,
            new_stock: newStock,
          });

        if (movementError) throw movementError;

        // Update material stock
        const { error: updateError } = await api
          .from("material_inventory")
          .update({
            current_stock: newStock,
            square_meters: (await api.from("material_inventory").select("square_meters").eq("id", materialId).single()).data?.square_meters + (sqmChange || 0),
            last_restock_date: new Date().toISOString().split("T")[0],
            updated_at: new Date().toISOString(),
          })
          .eq("id", materialId);

        if (updateError) throw updateError;

        toast.success(`Entrada registrada con nuevo lote: ${formData.batchNumber}`);
        queryClient.invalidateQueries({ queryKey: ["material-lots", materialId] });
        queryClient.invalidateQueries({ queryKey: ["material-inventory"] });
        queryClient.invalidateQueries({ queryKey: ["inventory-movements", materialId] });
        onOpenChange(false);
        resetForm();
        return;
      }

      // CASE 3: No lots - standard movement
      let newStock: number;
      let movementQuantity: number;

      if (movementType === "in") {
        newStock = currentStock + quantity;
        movementQuantity = quantity;
      } else if (movementType === "out") {
        newStock = currentStock - quantity;
        movementQuantity = -quantity;
      } else {
        newStock = quantity;
        movementQuantity = quantity - currentStock;
      }

      const { error: movementError } = await api
        .from("inventory_movements")
        .insert({
          material_inventory_id: materialId,
          movement_type: movementType === "adjustment" ? "adjustment" : movementType,
          quantity: movementQuantity,
          reason: formData.reason,
          notes: formData.notes || null,
          performed_by: user?.id,
          previous_stock: currentStock,
          new_stock: newStock,
        });

      if (movementError) throw movementError;

      const { error: updateError } = await api
        .from("material_inventory")
        .update({
          current_stock: newStock,
          last_restock_date: movementType === "in" ? new Date().toISOString().split("T")[0] : undefined,
          updated_at: new Date().toISOString(),
        })
        .eq("id", materialId);

      if (updateError) throw updateError;

      toast.success(
        movementType === "in"
          ? "Entrada registrada"
          : movementType === "out"
          ? "Salida registrada"
          : "Ajuste realizado"
      );

      queryClient.invalidateQueries({ queryKey: ["material-inventory", materialId] });
      queryClient.invalidateQueries({ queryKey: ["inventory-movements", materialId] });
      queryClient.invalidateQueries({ queryKey: ["material-inventory"] });

      onOpenChange(false);
      resetForm();
    } catch (error: any) {
      toast.error(error.message || "Error al registrar movimiento");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      quantity: "",
      squareMeters: "",
      reason: "",
      notes: "",
      lotId: "",
      batchNumber: "",
      rollNumber: "",
    });
    setLotMode("existing");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {getIcon()}
            {getTitle()}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Material: {materialName}
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Current Stock Info */}
          <div className="p-3 rounded-lg bg-muted/50">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Stock actual</span>
              <span className="font-bold text-lg">
                {currentStock} {unit}
              </span>
            </div>
          </div>

          {/* Lot Selection Mode */}
          {lotsLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Cargando lotes...
            </div>
          ) : (
            <div className="space-y-3">
              <Label>Lote / Rollo</Label>

              {/* Mode selector - only show for entries or if has lots */}
              {(movementType === "in" || hasLots) && (
                <RadioGroup
                  value={lotMode}
                  onValueChange={(v) => setLotMode(v as "existing" | "new")}
                  className="flex gap-4"
                >
                  {hasLots && (
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="existing" id="existing" />
                      <Label htmlFor="existing" className="cursor-pointer font-normal">
                        <Package className="w-4 h-4 inline mr-1" />
                        Lote existente
                      </Label>
                    </div>
                  )}
                  {movementType === "in" && (
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="new" id="new" />
                      <Label htmlFor="new" className="cursor-pointer font-normal">
                        <Plus className="w-4 h-4 inline mr-1" />
                        Nuevo lote
                      </Label>
                    </div>
                  )}
                </RadioGroup>
              )}

              {/* Existing lot selector */}
              {lotMode === "existing" && hasLots && (
                <Select
                  value={formData.lotId}
                  onValueChange={(value) =>
                    setFormData({ ...formData, lotId: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar lote" />
                  </SelectTrigger>
                  <SelectContent>
                    {lots?.map((lot) => (
                      <SelectItem key={lot.id} value={lot.id}>
                        <span className="font-medium">{lot.batch_number}</span>
                        {lot.roll_number && <span className="text-muted-foreground"> | R: {lot.roll_number}</span>}
                        <span className="text-muted-foreground"> | {lot.quantity} {unit}</span>
                        {lot.square_meters > 0 && <span className="text-muted-foreground"> | {lot.square_meters}m²</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* Selected lot info */}
              {lotMode === "existing" && selectedLot && (
                <div className="text-xs p-2 rounded bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800">
                  <div className="flex justify-between">
                    <span>Stock del lote:</span>
                    <span className="font-medium">{selectedLot.quantity} {unit}</span>
                  </div>
                  {selectedLot.square_meters > 0 && (
                    <div className="flex justify-between">
                      <span>m² del lote:</span>
                      <span className="font-medium">{selectedLot.square_meters} m²</span>
                    </div>
                  )}
                </div>
              )}

              {/* New lot fields */}
              {lotMode === "new" && movementType === "in" && (
                <div className="grid grid-cols-2 gap-3 p-3 rounded-lg border border-dashed">
                  <div className="space-y-1">
                    <Label className="text-xs">Número de Lote *</Label>
                    <Input
                      value={formData.batchNumber}
                      onChange={(e) =>
                        setFormData({ ...formData, batchNumber: e.target.value.toUpperCase() })
                      }
                      placeholder="Ej: LOT-2026-001"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Número de Rollo</Label>
                    <Input
                      value={formData.rollNumber}
                      onChange={(e) =>
                        setFormData({ ...formData, rollNumber: e.target.value })
                      }
                      placeholder="Ej: 1"
                    />
                  </div>
                </div>
              )}

              {/* No lots message for exits */}
              {!hasLots && movementType === "out" && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Este material no tiene lotes registrados. La salida se descontará del stock general.
                </p>
              )}
            </div>
          )}

          {/* Quantity */}
          <div className="space-y-2">
            <Label>
              {movementType === "adjustment" ? "Nuevo Stock *" : "Cantidad *"}
            </Label>
            <div className="relative">
              <Input
                type="number"
                min="0"
                step="0.001"
                value={formData.quantity}
                onChange={(e) =>
                  setFormData({ ...formData, quantity: e.target.value })
                }
                placeholder={movementType === "adjustment" ? "Cantidad total" : "0"}
                className={cn(
                  "pr-16",
                  movementType === "in" && "border-green-500 focus:ring-green-500",
                  movementType === "out" && "border-red-500 focus:ring-red-500"
                )}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                {unit}
              </span>
            </div>
            {movementType === "out" && (
              <p className="text-xs text-muted-foreground">
                Máximo disponible: {lotMode === "existing" && selectedLot ? selectedLot.quantity : currentStock} {unit}
              </p>
            )}
          </div>

          {/* Square Meters - for entries */}
          {movementType === "in" && (
            <div className="space-y-2">
              <Label>Metros Cuadrados</Label>
              <div className="relative">
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.squareMeters}
                  onChange={(e) =>
                    setFormData({ ...formData, squareMeters: e.target.value })
                  }
                  placeholder="0.00"
                  className="pr-12"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                  m²
                </span>
              </div>
            </div>
          )}

          {/* Reason */}
          <div className="space-y-2">
            <Label>Motivo *</Label>
            <Select
              value={formData.reason}
              onValueChange={(value) =>
                setFormData({ ...formData, reason: value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar motivo" />
              </SelectTrigger>
              <SelectContent>
                {reasons.map((reason) => (
                  <SelectItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notas</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              placeholder="Observaciones adicionales..."
              rows={2}
            />
          </div>

          {/* Preview */}
          {formData.quantity && !isNaN(parseFloat(formData.quantity)) && (
            <div className="p-3 rounded-lg border bg-muted/30">
              <p className="text-sm text-muted-foreground mb-1">
                Stock después del movimiento:
              </p>
              <p className="font-bold text-lg">
                {movementType === "in"
                  ? currentStock + parseFloat(formData.quantity)
                  : movementType === "out"
                  ? currentStock - parseFloat(formData.quantity)
                  : parseFloat(formData.quantity)}{" "}
                {unit}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                resetForm();
              }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading || registerLotMovement.isPending}
              className={cn(
                movementType === "in" && "bg-green-600 hover:bg-green-700",
                movementType === "out" && "bg-red-600 hover:bg-red-700"
              )}
            >
              {loading || registerLotMovement.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Procesando...
                </>
              ) : (
                "Confirmar"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
